import React, { useEffect, useRef, useState } from 'react';
import { fmtDate } from '../../lib/dates';
import { runAiJob } from '../../lib/aiJob';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Download, RefreshCw } from 'lucide-react';
import { StrategyPanel } from '../judicial/StrategyPanel';
import { saveBlob } from '../judicial/downloadPdf';
import { IntakeStrategyView } from './IntakeStrategyView';
import { SalaProfilePanel } from './SalaProfilePanel';

export const MatterStrategyDialog = ({ matter, open, onOpenChange, onUpdated, mode = 'strategy' }) => {
  const hearing = mode === 'hearing';
  const base = `/crm/matters/${matter.id}/${hearing ? 'hearing-brief' : 'strategy'}`;
  const initial = hearing ? (matter.hearing_brief?.for_hearing === matter.next_hearing ? matter.hearing_brief : null) : matter.ai_strategy;
  const { api } = useAuth();
  const [strategy, setStrategy] = useState(initial || null);
  const [loading, setLoading] = useState(false);
  const inFlight = useRef(false);

  const generate = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    try {
      const result = await runAiJob(api, base);
      setStrategy(result.strategy);
      onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Error al generar la estrategia');
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && !strategy && !loading) generate();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const download = async () => {
    try {
      const r = await api.get(`${base}/pdf`, { responseType: 'blob' });
      saveBlob(r.data, `${hearing ? 'audiencia' : 'estrategia'}-${matter.title}.pdf`);
    } catch {
      toast.error('No se pudo descargar el PDF');
    }
  };

  const actions = (
    <>
      <button data-testid="matter-strategy-regenerate-btn" onClick={generate} disabled={loading} className="text-xs border border-slate-600 text-slate-300 rounded-full px-3 py-1 flex items-center gap-1 disabled:opacity-50">
        <RefreshCw className="w-3 h-3" />Regenerar
      </button>
      <button data-testid="matter-strategy-pdf-btn" onClick={download} className="text-xs bg-amber-500 text-slate-950 font-semibold rounded-full px-3 py-1 flex items-center gap-1">
        <Download className="w-3 h-3" />PDF para cliente
      </button>
    </>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="matter-strategy-dialog" className="bg-slate-950 border-slate-700 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white">{matter.title}</DialogTitle>
          <DialogDescription className="text-slate-400">
            {hearing && matter.next_hearing ? `${matter.hearing_type || 'Audiencia'} · ${fmtDate(matter.next_hearing, true)} · ` : ''}Juez: {matter.assigned_judge_name || (matter.court ? 'por asignar' : 'jueces probables del país')} · {matter.court ? `${matter.court} · ` : ''}{matter.matter_type} · {matter.country}
          </DialogDescription>
        </DialogHeader>
        {!hearing && matter.intake_strategy && <IntakeStrategyView s={matter.intake_strategy} history={matter.intake_strategy_history || []} matter={matter} />}
        {!hearing && (matter.court_slug || matter.court) && <SalaProfilePanel matter={matter} onUpdated={onUpdated} />}
        <StrategyPanel label={hearing ? 'Informe de Audiencia' : 'Estrategia de Victoria'} strategy={loading ? null : strategy} loading={loading} actions={strategy && !loading ? actions : null} />
      </DialogContent>
    </Dialog>
  );
};
